// <localize-text> Web Component
//
// PR-i18n.1 of the i18n architecture follow-up to the CDN caching plan
// (memory/i18n_architecture_plan.md v3 §1, §5). Wraps every translated
// string on cacheable surfaces (/, /search, /property/*) so the user's
// preferred language can swap text post-paint without reloading.
//
// SSR pattern emitted by the {{tx}} / {{txf}} / {{txhtml}} / {{txhtmlf}}
// template helpers (handlers.go FuncMap):
//   <localize-text key="property.see_availability"
//                  data-ssr="See availability">See availability</localize-text>
// HTML-mode (txhtml*):
//   <localize-text key="property.couples_callout" data-html
//                  data-ssr-html="Couples ... <strong>9.2</strong>">
//                  Couples ... <strong>9.2</strong></localize-text>
//
// The data-ssr / data-ssr-html attributes hold the SSR fallback
// redundantly with the inner content. JS reads from the attributes
// (NOT textContent) because component scripts load synchronously in
// <head>, so the element upgrades on the start tag — BEFORE child
// text is parsed. Attributes ARE available at start-tag time, so
// dataset.* is reliable. (Same hazard documented in price-display.js
// _ssrEquivalentText.)
//
// On 'lang-change' event re-evaluate. If target===en-us OR no
// translation found OR transient fetch error → restore from
// data-ssr / data-ssr-html. Otherwise swap to dict[key] (or
// printf-formatted dict[key] using data-args).

(function () {
    const I18N_TTL_MS = 24 * 60 * 60 * 1000; // 24h, matches /api/i18n s-maxage=86400
    const CACHE_KEY_PREFIX = 'i18n_v1_';

    let _inFlight = new Map(); // lang → Promise<{dict, definitive}>

    function _readCache(lang) {
        try {
            const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + lang);
            if (!raw) return null;
            const obj = JSON.parse(raw);
            if (!obj || (Date.now() - obj.t) > I18N_TTL_MS) {
                sessionStorage.removeItem(CACHE_KEY_PREFIX + lang);
                return null;
            }
            return obj.dict;
        } catch (e) { return null; }
    }

    function _writeCache(lang, dict) {
        try {
            sessionStorage.setItem(CACHE_KEY_PREFIX + lang, JSON.stringify({ t: Date.now(), dict: dict }));
        } catch (e) { /* ignore */ }
    }

    // Returns Promise<{dict|null, definitive}>. definitive=false on
    // network/server error → callers leave SSR text alone (no churn
    // on transient failures). 404 (unsupported lang) → definitive=true,
    // dict=null (caller treats as no-translation-available, restores SSR).
    function _fetchI18n(lang) {
        const cached = _readCache(lang);
        if (cached) return Promise.resolve({ dict: cached, definitive: true });
        if (_inFlight.has(lang)) return _inFlight.get(lang);
        const p = fetch('/api/i18n/' + encodeURIComponent(lang), { credentials: 'same-origin' })
            .then(function (resp) {
                if (resp.status === 200) {
                    return resp.json().then(function (obj) {
                        return { dict: obj.dict || {}, definitive: true };
                    });
                }
                if (resp.status === 404) return { dict: null, definitive: true };
                return { dict: null, definitive: false };
            })
            .then(function (r) {
                if (r.definitive && r.dict) _writeCache(lang, r.dict);
                return r;
            })
            .catch(function () { return { dict: null, definitive: false }; })
            .then(function (r) { _inFlight.delete(lang); return r; });
        _inFlight.set(lang, p);
        return p;
    }

    function _preferredLang() {
        try {
            const ls = localStorage.getItem('preferred_language');
            if (ls) return ls.toLowerCase();
        } catch (e) {}
        const m = document.cookie.match(/(?:^|;\s*)preferred_language=([^;]+)/);
        return m ? decodeURIComponent(m[1]).toLowerCase() : '';
    }

    // printf-stub: handles %s, %d, %.Nf, %% (per en.json's actual
    // format directives — see memory/i18n_architecture_plan.md v3 §3.2).
    // Left-to-right scan; %% escapes BEFORE %d/%s/%.Nf substitutions
    // so combinations like "%d%%" produce "5%" not "5%5".
    function _printfStub(fmt, args) {
        let i = 0;
        let out = '';
        let p = 0;
        while (p < fmt.length) {
            const ch = fmt[p];
            if (ch !== '%') { out += ch; p++; continue; }
            // %% literal
            if (fmt[p + 1] === '%') { out += '%'; p += 2; continue; }
            // %.Nf
            const m = fmt.slice(p).match(/^%(?:\.(\d+))?([sdf])/);
            if (!m) { out += ch; p++; continue; }
            const verb = m[2];
            const v = args[i++];
            if (verb === 's') {
                out += String(v == null ? '' : v);
            } else if (verb === 'd') {
                out += String(Math.trunc(Number(v) || 0));
            } else if (verb === 'f') {
                const dec = m[1] ? parseInt(m[1], 10) : 6;
                out += (Number(v) || 0).toFixed(dec);
            }
            p += m[0].length;
        }
        return out;
    }

    // Single module-level pageshow listener. With ~290
    // <localize-text> elements per page, a per-element listener would
    // fire 290 times on every bfcache restore — wasteful. One handler
    // re-dispatches lang-change which the module-level lang-change
    // listener below handles via a single querySelectorAll walk.
    window.addEventListener('pageshow', function (e) {
        if (e.persisted) {
            document.dispatchEvent(new CustomEvent('lang-change', { bubbles: true }));
        }
    });

    // PR-perf.2: Module-level lang-change listener replaces the
    // per-instance pattern. lang-change is user-initiated (lang-switcher
    // click) or bfcache-driven (above) — both rare — so the DOM walk
    // on dispatch is cheap compared to N persistent listeners.
    document.addEventListener('lang-change', function () {
        const els = document.querySelectorAll('localize-text');
        for (let i = 0; i < els.length; i++) {
            if (els[i]._reformat) els[i]._reformat();
        }
    });

    class LocalizeText extends HTMLElement {
        connectedCallback() {
            if (this._wiredOnce) return;
            this._wiredOnce = true;
            this._key = this.getAttribute('key') || '';
            this._isHtml = this.hasAttribute('data-html');
            // Read SSR fallback from data attribute (reliable at
            // upgrade time; textContent would be empty when scripts
            // load in <head>).
            this._ssr = this._isHtml
                ? (this.dataset.ssrHtml || '')
                : (this.dataset.ssr || '');
            this._args = this.dataset.args || '';
            const self = this;
            // PR-perf.2: lang-change handled at module scope (see below).
            // Per-element readyState-aware gate for first reformat.
            // setTimeout(0) (the previous fix) proved insufficient under
            // cache-hit conditions in <user-menu> — the macrotask fired
            // BEFORE the parser appended SSR children, so the
            // textContent write was followed by parser-appended SSR text
            // → doubled output. DCL gating is robust: defers until full
            // document parsed (children present), OR runs immediately
            // for dynamic insertions (e.g. via /api/availability/<slug>
            // fragment or search.js load-more cards — innerHTML inserts
            // children atomically before custom-element reactions fire).
            // memory/hydration_race_fixes_plan.md §3.D.
            const run = function () { self._reformat(); };
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run, { once: true });
            } else {
                run();
            }
        }

        _reformat() {
            const target = _preferredLang();
            const wasFirst = !this._reformatRanOnce;
            this._reformatRanOnce = true;
            // No pref OR target===SSR canonical (en-us) → SSR is canonical.
            if (!target || target === 'en-us') {
                // PR-perf.1: on the first reformat, SSR text is already
                // in the DOM (server-rendered) — skip the no-op
                // _writeContent (diff-skip neutralizes the actual DOM
                // write, but skipping here saves textContent reads ×
                // N elements at DCL). On subsequent calls (e.g.
                // lang-change from fr → no-pref), restore SSR explicitly.
                if (!wasFirst) this._writeContent(this._ssr);
                return;
            }
            const self = this;
            _fetchI18n(target).then(function (r) {
                if (!r.definitive) return; // transient — leave displayed text alone
                // Stale-response guard: a user who flips fr → de
                // quickly can have the slower fr fetch resolve AFTER
                // the de fetch already wrote content. Drop stale
                // results — the active fetch's resolution will write
                // the correct text. (Without this, a slow fr response
                // overwrites de output until the next lang-change.)
                if (_preferredLang() !== target) return;
                if (!r.dict) {
                    // 404 — lang not supported; restore SSR.
                    self._writeContent(self._ssr);
                    return;
                }
                let value = r.dict[self._key];
                if (value == null) {
                    // Missing key in target lang's dict — restore SSR
                    // (which was rendered with i18n.T's en→base→exact
                    // fallback chain; same semantic as server-side T).
                    self._writeContent(self._ssr);
                    return;
                }
                if (self._args) {
                    try {
                        const parsedArgs = JSON.parse(self._args);
                        value = _printfStub(value, parsedArgs);
                    } catch (e) {
                        // data-args malformed (shouldn't happen — emitted
                        // by the Go template helper). Fall back to SSR.
                        self._writeContent(self._ssr);
                        return;
                    }
                }
                self._writeContent(value);
            });
        }

        _writeContent(value) {
            // Defensive parser-race guard: skip if children not parsed
            // yet AND we're trying to write non-empty value AND document
            // is still loading. Per-element readyState gating in
            // connectedCallback should make this unreachable today; this
            // is defense-in-depth against future regressions or any
            // pre-DCL caller path (e.g. early `lang-change` event).
            const cur = this._isHtml ? this.innerHTML : this.textContent;
            if (!cur && value && document.readyState === 'loading') return;
            // Diff-skip: only mutate the DOM if the new value differs
            // from what's already in the element. Micro-optimization for
            // the common case where SSR text already matches.
            if (this._isHtml) {
                if (this.innerHTML !== value) this.innerHTML = value;
            } else {
                if (this.textContent !== value) this.textContent = value;
            }
        }
    }

    if (!customElements.get('localize-text')) {
        customElements.define('localize-text', LocalizeText);
    }
})();
